import { Injectable, inject } from '@angular/core';
import {ISearchResponseModel} from '@app/shared/models/search-response.model';
import {AlgoliaSearchService} from '@app/shared/services/algolia-search.service';
import {environment} from '@environments/environment';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, map, of, switchMap} from 'rxjs';
import {searchAction, searchEffectFailure, searchEffectSuccess} from './search.actions';

@Injectable()
export class SearchEffects {
  private readonly actions$ = inject(Actions);
  private readonly algoliaService = inject(AlgoliaSearchService);


  search$ = createEffect(() =>
    this.actions$.pipe(
      ofType(searchAction),
      switchMap(({request}) => {
        const {term, pageNumber, pageSize, organizationId, countryId} = request;

        return this.getSearchResults(term, pageNumber, pageSize, organizationId, countryId).pipe(
          map((results: ISearchResponseModel) => searchEffectSuccess({response: results})),
          catchError(err => of(searchEffectFailure({reason: err?.message ?? 'Search failed'})))
        );
      })
    )
  );

  private getSearchResults(
    term: string,
    pageNumber: number,
    pageSize: number,
    organizationId: number,
    countryId: number
  ) {
    return this.algoliaService
      .multiIndexSearch([
        {
          indexName: environment.algoliaSearch.variantsIndexName,
          query: term,
          params: {
            getRankingInfo: true,
            filters: `objectType:option AND (hasCountryRestrictions:false OR countryRestrictions:${countryId}) AND (hasOrganisationRestrictions:false OR organizationRestrictions:${organizationId})`,
          },
        },
        {
          indexName: environment.algoliaSearch.productsIndexName,
          query: term,
          params: {
            getRankingInfo: true,
            filters: `objectType:product AND (hasCountryRestrictions:false OR countryRestrictions:${countryId}) AND (hasOrganisationRestrictions:false OR organizationRestrictions:${organizationId})`,
          },
        },
      ])
      .pipe(
        map(res => {
          const variants = res.results[0].hits;
          const products = res.results[1].hits;

          const merged = [...variants, ...products].map(hit => ({
            ...hit,
            name: hit.optionName ?? hit.productName,
            description: hit.productDescription ?? hit.optionDescription,
            genes: hit.genes ?? [],
            searchTerm: term,
          }));

          merged.sort((a, b) => {
            if (b._rankingInfo.nbExactWords !== a._rankingInfo.nbExactWords) {
              return b._rankingInfo.nbExactWords - a._rankingInfo.nbExactWords;
            }
            if (b._rankingInfo.nbTypos !== a._rankingInfo.nbTypos) {
              return a._rankingInfo.nbTypos - b._rankingInfo.nbTypos;
            }
            return a._rankingInfo.proximityDistance - b._rankingInfo.proximityDistance;
          });

          const totalCount = merged.length;
          const start = (pageNumber - 1) * pageSize;
          const paginated = merged.slice(start, start + pageSize);

          return {
            query: term,
            pageNumber,
            pageSize,
            pageCount: Math.ceil(totalCount / pageSize),
            totalCount,
            results: paginated,
          };
        })
      );
  }
}
